import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, FormInstance, Spin, Checkbox, Upload, Row, Col, message, Collapse, Button } from "antd";
import { UploadFile, UploadFileStatus } from "antd/es/upload/interface";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import { Question, Document } from "../../../../types";
import { getCategories, getQuestions, getSubCategories } from "../../../../redux/features/generaleWebData/generaleWebDataAsyncThunk";
import { fetchQuestions } from "../../../../redux/features/question/questionAsyncThunk";
import { setQuestionData } from "../../../../redux/features/question/questionSlice";
import { FormWrapper } from "../../../../components/styles";
import Dropdown from "../../../../components/Dropdown";
import { TextArea } from "../../../../components/Input";
import { Label } from "../../../../components/Input/styles";
import { capitalizeWord } from "../../../../utils/helpers/capitalizeWord";
import { sortOptionsAlphabetically } from "../../../../utils/helpers/sortOptionsAlphabetically";
import { imageUploadAccept, validateUploadFiles } from "../../../../utils/helpers/validateUploadFiles";

// Extend the Report interface to include formRef property
interface Report {
  id?: string;
  formRef?: {
    current: {
      prepareItemData?: () => void;
      [key: string]: any;
    };
  };
  [key: string]: any;
}

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

interface CustomUploadFile extends UploadFile<any> {
  binary?: File;
}

interface ItemDetailsFormProps {
  form: FormInstance;
  report: Report | null;
}

// Define interface for answer object
interface Answer {
  question: string;
  answer: string | boolean;
}

// Define interface for item object
interface Item {
  id?: string;
  category: string | { id: string; name: string };
  subCategory: string | { id: string; name: string };
  itemDescription: string;
  // lostDate: any;
  documents: Document[];
  answers: Answer[];
}

const ItemDetailsForm = ({ form, report }: ItemDetailsFormProps): React.ReactElement => {
  // Reference to expose methods to parent component
  const prepareFormData = () => {
    return updateAllItemsAnswers();
  };

  // Expose the prepareFormData method to parent component
  if (report?.formRef) {
    report.formRef.current = {
      ...report.formRef.current,
      prepareItemData: prepareFormData
    };
  }

  const dispatch = useAppDispatch();
  const [questionLoading, setQuestionLoading] = useState(false);
  const [fileList, setFileList] = useState<{ [key: number]: CustomUploadFile[] }>({});
  const { categoryState, subCategoryState } = useAppSelector((state) => state.generaleWebData);
  const { categories, status: categoryStatus } = useMemo(() => categoryState, [categoryState]);
  const { subCategories, status: subCategoryStatus } = useMemo(() => subCategoryState, [subCategoryState]);

  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
  const [checkedValues, setCheckedValues] = useState<{ [key: string]: boolean | string }>({});
  const [itemsList, setItemsList] = useState<Item[]>([]);
  const [itemQuestions, setItemQuestions] = useState<{ [key: number]: Question[] }>({});

  const items = Form.useWatch('items', form);

  const handleSelectChange = (itemIndex: number, questionId: string, value: string) => {
    try {
      // Update the selected options state
      setSelectedOptions((prev: Record<string, any>) => ({
        ...prev,
        [`${itemIndex}_${questionId}`]: value,
      }));

      // Find the question to get its options
      const question = itemQuestions[itemIndex]?.find(q => q.id === questionId);
      if (!question) {
        console.error(`Question with ID ${questionId} not found for item ${itemIndex}`);
        return;
      }

      // Find the selected option to get its name
      const selectedOption = question.options?.find((opt: any) => opt.id === value);
      if (!selectedOption) {
        console.error(`Option with ID ${value} not found for question ${questionId}`);
        return;
      }

      // Update the form field with the option name (which is what the API expects)
      form.setFieldsValue({ [`item_${itemIndex}_question_${questionId}`]: selectedOption.name });

      // Update the item's answers array
      setItemsList(prev => {
        const updatedItems = prev.map((item, idx) => {
          if (idx === itemIndex) {
            // Find if there's an existing answer for this question
            const existingAnswerIndex = item.answers?.findIndex((a: any) => a.question === questionId);
            const newAnswers = [...(item.answers || [])];

            if (existingAnswerIndex !== -1 && existingAnswerIndex !== undefined) {
              // Update existing answer
              newAnswers[existingAnswerIndex] = {
                ...newAnswers[existingAnswerIndex],
                answer: selectedOption.name
              };
            } else {
              // Add new answer
              newAnswers.push({
                question: questionId,
                answer: selectedOption.name
              });
            }

            return { ...item, answers: newAnswers };
          }
          return item;
        });

        // Update the form's items field to ensure it has the latest data
        form.setFieldsValue({ items: updatedItems });

        return updatedItems;
      });

      // Clear any previously set subquestion fields for this question
      const formUpdate: { [key: string]: any } = {};

      // Clear all subquestion fields for other options of this question
      question.options?.forEach((option: any) => {
        if (option.id !== value) { // Skip the selected option
          option.subQuestion?.forEach((subQ: any) => {
            formUpdate[`item_${itemIndex}_subQuestion_${subQ.id}`] = undefined;
          });
        }
      });

      if (Object.keys(formUpdate).length > 0) {
        form.setFieldsValue(formUpdate);
      }
    } catch (error) {
      console.error('Error in handleSelectChange:', error);
    }
  };

  const handleCheckboxChange = (itemIndex: number, questionId: string, e: any) => {
    try {
      const isChecked = e.target.checked;
      const answerValue = isChecked ? "true" : "false";

      setCheckedValues(prev => ({
        ...prev,
        [`${itemIndex}_${questionId}`]: answerValue,
      }));

      // Update the form field
      form.setFieldsValue({ [`item_${itemIndex}_question_${questionId}`]: isChecked });

      // Find the question to get its options
      const question = itemQuestions[itemIndex]?.find(q => q.id === questionId);
      if (!question) {
        console.error(`Question with ID ${questionId} not found for item ${itemIndex}`);
        return;
      }

      // Update the item's answers array
      setItemsList(prev => {
        const updatedItems = prev.map((item, idx) => {
          if (idx === itemIndex) {
            // Find if there's an existing answer for this question
            const existingAnswerIndex = item.answers?.findIndex((a: any) => a.question === questionId);
            const newAnswers = [...(item.answers || [])];

            if (existingAnswerIndex !== -1 && existingAnswerIndex !== undefined) {
              // Update existing answer
              newAnswers[existingAnswerIndex] = {
                ...newAnswers[existingAnswerIndex],
                answer: answerValue
              };
            } else {
              // Add new answer
              newAnswers.push({
                question: questionId,
                answer: answerValue
              });
            }

            return { ...item, answers: newAnswers };
          }
          return item;
        });

        // Update the form's items field to ensure it has the latest data
        form.setFieldsValue({ items: updatedItems });

        return updatedItems;
      });

      // Handle subquestions based on checkbox state
      const formUpdate: { [key: string]: any } = {};

      // Clear all subquestion fields if checkbox is unchecked
      if (!isChecked) {
        question.options?.forEach((option: any) => {
          if (option.name === "true") { // Only true option has subquestions for checkboxes
            option.subQuestion?.forEach((subQ: any) => {
              formUpdate[`item_${itemIndex}_subQuestion_${subQ.id}`] = undefined;
            });
          }
        });

        if (Object.keys(formUpdate).length > 0) {
          form.setFieldsValue(formUpdate);
        }
      }
    } catch (error) {
      console.error('Error in handleCheckboxChange:', error);
    }
  };

  Form.useWatch([], form);

  useEffect(() => {
    if (categoryStatus === "idle" && categories.length === 0) {
      dispatch(getCategories());
    }
  }, [dispatch]);

  useEffect(() => {
    // Always load subcategories on mount
    dispatch(getSubCategories({}));
  }, [dispatch]);

  const categoryOptions = useMemo(() => {
    return categories.map((category) => ({
      label: category.name,
      value: category.id,
      key: category.id,
    }));
  }, [categories]);

  const getSubCategoryOptions = (itemIndex: number): any[] => {
    if (!itemsList || !itemsList[itemIndex]) return [];

    // Handle both object and string ID formats
    const selectedCategory = itemsList[itemIndex]?.category;
    let selectedCategoryId = selectedCategory;

    // If category is an object with an id property, extract the id
    if (selectedCategory && typeof selectedCategory === 'object' && selectedCategory.id) {
      selectedCategoryId = selectedCategory.id;
    }

    if (!selectedCategoryId || !subCategories?.length) {
      return [];
    }

    // Filter subcategories based on the category ID
    const filtered = subCategories.filter(subCategory => {
      // Handle case where subCategory.category could be an object or string
      const subCatCategoryId =
        subCategory?.category?.id ||
        (typeof subCategory?.category === 'string' ? subCategory.category : null);

      return subCatCategoryId === selectedCategoryId;
    }) || [];

    return filtered.map((subCategory) => ({
      label: subCategory.name,
      value: subCategory.id,
      key: subCategory.id,
    }));
  };

  const getQuestionsHandle = async (itemIndex: number, subCategoryId: string) => {
    setQuestionLoading(true);
    try {
      const response: any = await dispatch(fetchQuestions({ search_query: '', offset: 0, limit: 1000, subCategoryId }));
      const questions = (response?.payload?.data as Question[]) || [];

      // Update the questions state
      setItemQuestions(prev => ({
        ...prev,
        [itemIndex]: questions
      }));

      // If we have questions, prepare the form fields for them
      if (questions.length > 0) {
        // Get the current item to check if it has existing answers
        const currentItem = itemsList[itemIndex];
        const formUpdate: { [key: string]: any } = {};

        // For each question, set up the form field with default empty value
        questions.forEach((question: Question) => {
          // Check if there's an existing answer for this question
          const existingAnswer = currentItem?.answers?.find((a: any) => a.question === question.id);

          if (existingAnswer) {
            // If there's an existing answer, use it
            if (question.fieldType === 2) { // Checkbox
              formUpdate[`item_${itemIndex}_question_${question.id}`] = existingAnswer.answer === "true";
              setCheckedValues(prev => ({
                ...prev,
                [`${itemIndex}_${question.id}`]: existingAnswer.answer
              }));
            } else if (question.fieldType === 3) { // Dropdown
              formUpdate[`item_${itemIndex}_question_${question.id}`] = existingAnswer.answer;

              // Find the option that matches the answer
              const selectedOption = question.options?.find(
                (option: any) => option.name === existingAnswer.answer
              );

              if (selectedOption) {
                setSelectedOptions((prev: Record<string, any>) => ({
                  ...prev,
                  [`${itemIndex}_${question.id}`]: selectedOption.id
                }));
              }
            } else { // Text input
              formUpdate[`item_${itemIndex}_question_${question.id}`] = existingAnswer.answer;
            }
          } else {
            // No existing answer, set default empty value
            formUpdate[`item_${itemIndex}_question_${question.id}`] = question.fieldType === 2 ? false : '';
          }
        });

        // Update the form with all the question fields
        form.setFieldsValue(formUpdate);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setQuestionLoading(false);
    }
  };

  useEffect(() => {
    if (items) {
      setItemsList(items);
    } else if (report?.items && report.items.length > 0) {
      // Process report items to ensure proper data structure
      const processedItems = report.items.map((item: any, itemIndex: number) => {
        return {
          ...item,
          category: typeof item.category === 'object' ? item.category?.id : item.category,
          subCategory: typeof item.subCategory === 'object' ? item.subCategory?.id : item.subCategory,
          itemDescription: item.itemDescription || '',
          // lostDate: item.lostDate ? dayjs(item.lostDate) : (report?.lostDate ? dayjs(report.lostDate) : null),
          documents: item.documents || [],
          answers: item.answers || []
        };
      });

      setItemsList(processedItems);
    } else {
      // Initialize with empty item if no items exist
      setItemsList([{
        category: '',
        subCategory: '',
        itemDescription: '',
        // lostDate: report?.lostDate ? dayjs(report.lostDate) : null,
        documents: [],
        answers: []
      }]);
    }
  }, [items, report]);

  // Separate useEffect to set form fields after subcategories are loaded
  useEffect(() => {
    if (report?.items && report.items.length > 0 && subCategories.length > 0) {
      // Add a small delay to ensure Redux state is fully updated
      const timer = setTimeout(() => {
        const formData: { [key: string]: any } = {};
        const newFileList: { [key: number]: CustomUploadFile[] } = {};
        const newSelectedOptions: Record<string, any> = {};
        const newCheckedValues: { [key: string]: boolean | string } = {};
        const newItemQuestions: { [key: number]: Question[] } = {};

        report.items.forEach((item: any, itemIndex: number) => {
          // Set basic item data - ensure we're using IDs, not objects
          const categoryId = typeof item.category === 'object' ? item.category?.id : item.category;
          const subCategoryId = typeof item.subCategory === 'object' ? item.subCategory?.id : item.subCategory;

          formData[`item_${itemIndex}_category`] = categoryId;
          formData[`item_${itemIndex}_subCategory`] = subCategoryId;
          formData[`item_${itemIndex}_itemDescription`] = item.itemDescription;
          // formData[`item_${itemIndex}_lostDate`] = item.lostDate ? dayjs(item.lostDate) : dayjs(report.lostDate);

          // Set documents
          if (item.documents && item.documents.length > 0) {
            const documentFiles = item.documents.map((doc: any) => ({
              uid: doc.id?.toString() || `-${Math.random()}`,
              name: doc.documentName || "Document",
              status: "done" as UploadFileStatus,
              url: doc.fullUrl,
              type: "image/jpeg",
            }));
            newFileList[itemIndex] = documentFiles;
          }

          // Set questions and answers
          if (item.subCategory?.questions) {
            newItemQuestions[itemIndex] = item.subCategory.questions;
            item.subCategory.questions.forEach((question: any) => {
              const fieldName = `item_${itemIndex}_question_${question.id}`;
              // Find the answer for this question from the question's answers object
              const questionAnswer = question.answers;
              const answerValue = questionAnswer?.answer;

              if (question.fieldType === 2) { // Checkbox
                const boolValue = answerValue === "true" || answerValue === true;
                formData[fieldName] = boolValue;
                newCheckedValues[`${itemIndex}_${question.id}`] = boolValue ? "true" : "false";
              } else if (question.fieldType === 3) { // Dropdown
                formData[fieldName] = answerValue;
                const selectedOption = question.options?.find(
                  (option: any) => option.name === answerValue || option.id === answerValue
                );
                if (selectedOption) {
                  newSelectedOptions[`${itemIndex}_${question.id}`] = selectedOption.id;
                }
              } else { // Text input
                formData[fieldName] = answerValue || '';
              }

              // Handle subQuestions
              question.options?.forEach((option: any) => {
                option.subQuestion?.forEach((subQuestion: any) => {
                  // Find the answer for this subquestion from the item's answers array
                  const subQuestionAnswer = item.answers?.find((answer: any) => answer.question === subQuestion.id);
                  formData[`item_${itemIndex}_subQuestion_${subQuestion.id}`] = subQuestionAnswer?.answer || '';
                });
              });
            });
          }
        });
        // Extract time of day label
        setFileList(newFileList);
        setSelectedOptions(newSelectedOptions);
        setCheckedValues(newCheckedValues);
        setItemQuestions(newItemQuestions);

        form.setFieldsValue(formData);
      }, 200); // Increased delay to ensure state is fully updated

      return () => clearTimeout(timer);
    }
  }, [report, subCategories, form]);

  const handleFileChange = async (itemIndex: number, { fileList: newFileList }: { fileList: CustomUploadFile[] }) => {
    setFileList(prev => ({
      ...prev,
      [itemIndex]: newFileList.slice(0, 3)
    }));

    const blobs = newFileList
      .map((file: UploadFile) => {
        if (file.originFileObj) {
          return new Blob([file.originFileObj], { type: file.originFileObj.type });
        }
        return null;
      })
      .filter((blob: any) => blob !== null);
    form.setFieldsValue({ [`item_${itemIndex}_documents`]: blobs });
  };

  const beforeUpload = (file: File) => {
    const isImg = file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png" || file.type === "image/gif" || file.type === "image/webp";
    const isLt1M = file.size / 1024 / 1024 <= 1;

    if (!isImg) {
      message.error('You can only upload JPG/JPEG/PNG/GIF/WEBP file formats!');
      return Upload.LIST_IGNORE;
    }

    if (!isLt1M) {
      message.error('Image must be smaller than 1MB!');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handlePreview = async (file: CustomUploadFile) => {
    if (file.url) {
      window.open(file.url, "_blank");
    }
  };

  const handleRemove = (itemIndex: number, file: CustomUploadFile) => {
    const item = report?.items?.[itemIndex];
    const documentId = item?.documents?.find((doc: any) => doc.id.toString() === file.uid)?.id;
    if (documentId && file?.uid && report?.id?.length) {
      const currentDeletedFiles = form.getFieldValue('deletedFiles') || [];
      form.setFieldsValue({
        deletedFiles: [...currentDeletedFiles, file.uid]
      });
    }
  };

  const onChangeCategory = (itemIndex: number, value: any) => {
    try {
      // Update the itemsList state with the new category
      setItemsList(prev =>
        prev.map((item: any, idx: number) =>
          idx === itemIndex
            ? {
              ...item,
              category: value,
              subCategory: '',
              answers: []
            }
            : item
        )
      );

      // Clear form fields for subcategory and any question answers
      const formUpdate: { [key: string]: any } = {
        [`item_${itemIndex}_category`]: value,
        [`item_${itemIndex}_subCategory`]: null
      };

      // Clear any existing question fields for this item
      if (itemQuestions[itemIndex]) {
        itemQuestions[itemIndex].forEach((question: Question) => {
          formUpdate[`item_${itemIndex}_question_${question.id}`] = undefined;

          // Also clear any subquestion fields
          question.options?.forEach((option: any) => {
            option.subQuestion?.forEach((subQ: any) => {
              formUpdate[`item_${itemIndex}_subQuestion_${subQ.id}`] = undefined;
            });
          });
        });
      }

      form.setFieldsValue(formUpdate);

      // Clear questions for this item
      setItemQuestions(prev => ({ ...prev, [itemIndex]: [] }));

      // Clear selected options and checked values for this item
      const newSelectedOptions = { ...selectedOptions };
      const newCheckedValues = { ...checkedValues };

      Object.keys(newSelectedOptions).forEach(key => {
        if (key.startsWith(`${itemIndex}_`)) {
          delete newSelectedOptions[key];
        }
      });

      Object.keys(newCheckedValues).forEach(key => {
        if (key.startsWith(`${itemIndex}_`)) {
          delete newCheckedValues[key];
        }
      });

      setSelectedOptions(newSelectedOptions);
      setCheckedValues(newCheckedValues);

      // Update the form's items field to ensure it has the latest data
      form.setFieldsValue({
        items: itemsList.map((item, idx) => {
          if (idx === itemIndex) {
            return { ...item, category: value, subCategory: '', answers: [] };
          }
          return item;
        })
      });
    } catch (error) {
      console.error('Error in onChangeCategory:', error);
    }
  };

  const onChangeSubCategory = async (itemIndex: number, subCategoryId: string) => {
    try {
      // Update the itemsList state with the new subcategory
      setItemsList(prev =>
        prev.map((item: any, idx: number) =>
          idx === itemIndex
            ? {
              ...item,
              subCategory: subCategoryId,
              answers: []
            }
            : item
        )
      );

      // Clear any existing question fields for this item
      const formUpdate: { [key: string]: any } = {
        [`item_${itemIndex}_subCategory`]: subCategoryId
      };

      // Clear any existing question fields for this item
      if (itemQuestions[itemIndex]) {
        itemQuestions[itemIndex].forEach((question: Question) => {
          formUpdate[`item_${itemIndex}_question_${question.id}`] = undefined;

          // Also clear any subquestion fields
          question.options?.forEach((option: any) => {
            option.subQuestion?.forEach((subQ: any) => {
              formUpdate[`item_${itemIndex}_subQuestion_${subQ.id}`] = undefined;
            });
          });
        });
      }

      form.setFieldsValue(formUpdate);

      // Clear selected options and checked values for this item
      const newSelectedOptions = { ...selectedOptions };
      const newCheckedValues = { ...checkedValues };

      Object.keys(newSelectedOptions).forEach(key => {
        if (key.startsWith(`${itemIndex}_`)) {
          delete newSelectedOptions[key];
        }
      });

      Object.keys(newCheckedValues).forEach(key => {
        if (key.startsWith(`${itemIndex}_`)) {
          delete newCheckedValues[key];
        }
      });

      setSelectedOptions(newSelectedOptions);
      setCheckedValues(newCheckedValues);

      // Load questions for the new subcategory
      await getQuestionsHandle(itemIndex, subCategoryId);

      // Update the form's items field to ensure it has the latest data
      form.setFieldsValue({
        items: itemsList.map((item, idx) => {
          if (idx === itemIndex) {
            return { ...item, subCategory: subCategoryId, answers: [] };
          }
          return item;
        })
      });
    } catch (error) {
      console.error('Error in onChangeSubCategory:', error);
    }
  };

  const onAddItem = () => {
    setItemsList([
      ...itemsList,
      {
        category: '',
        subCategory: '',
        itemDescription: '',
        // lostDate: report?.lostDate ? dayjs(report.lostDate) : null,
        documents: [],
        answers: []
      }
    ]);
  };

  const onRemoveItem = (itemIndex: number) => {
    if (itemsList.length > 1) {
      const itemToRemove = itemsList[itemIndex];

      // Track deleted items for API
      if (itemToRemove.id) {
        const currentDeletedItems = form.getFieldValue('deletedItems') || [];
        form.setFieldsValue({
          deletedItems: [...currentDeletedItems, itemToRemove.id]
        });
      }

      // Remove the item from the list
      const updatedItems = itemsList.filter((_, index) => index !== itemIndex);
      setItemsList(updatedItems);

      // Update form items field
      form.setFieldsValue({ items: updatedItems });

      // Get all current form values
      const currentFormValues = form.getFieldsValue();
      const newFormValues: { [key: string]: any } = {};

      // Reindex all form fields for remaining items
      updatedItems.forEach((item, newIndex) => {
        const oldIndex = itemsList.findIndex(originalItem => originalItem === item);

        if (oldIndex !== -1 && oldIndex !== newIndex) {
          // Move form values from old index to new index
          Object.keys(currentFormValues).forEach(fieldName => {
            if (fieldName.startsWith(`item_${oldIndex}_`)) {
              const newFieldName = fieldName.replace(`item_${oldIndex}_`, `item_${newIndex}_`);
              newFormValues[newFieldName] = currentFormValues[fieldName];
              newFormValues[fieldName] = undefined; // Clear old field
            }
          });
        } else if (oldIndex === newIndex) {
          // Keep existing values for items that don't need reindexing
          Object.keys(currentFormValues).forEach(fieldName => {
            if (fieldName.startsWith(`item_${oldIndex}_`)) {
              newFormValues[fieldName] = currentFormValues[fieldName];
            }
          });
        }
      });

      // Clear form fields for the removed item and any items beyond the new length
      for (let i = updatedItems.length; i < itemsList.length; i++) {
        Object.keys(currentFormValues).forEach(fieldName => {
          if (fieldName.startsWith(`item_${i}_`)) {
            newFormValues[fieldName] = undefined;
          }
        });
      }

      form.setFieldsValue(newFormValues);

      // Reindex state objects
      const newSelectedOptions: { [key: string]: any } = {};
      const newCheckedValues: { [key: string]: any } = {};
      const newItemQuestions: { [key: number]: Question[] } = {};
      const newFileList: { [key: number]: any[] } = {};

      updatedItems.forEach((item, newIndex) => {
        const oldIndex = itemsList.findIndex(originalItem => originalItem === item);

        if (oldIndex !== -1) {
          // Move state entries to new index
          Object.keys(selectedOptions).forEach(key => {
            if (key.startsWith(`${oldIndex}_`)) {
              const newKey = key.replace(`${oldIndex}_`, `${newIndex}_`);
              newSelectedOptions[newKey] = selectedOptions[key];
            }
          });

          Object.keys(checkedValues).forEach(key => {
            if (key.startsWith(`${oldIndex}_`)) {
              const newKey = key.replace(`${oldIndex}_`, `${newIndex}_`);
              newCheckedValues[newKey] = checkedValues[key];
            }
          });

          if (itemQuestions[oldIndex]) {
            newItemQuestions[newIndex] = itemQuestions[oldIndex];
          }

          if (fileList[oldIndex]) {
            newFileList[newIndex] = fileList[oldIndex];
          }
        }
      });

      setSelectedOptions(newSelectedOptions);
      setCheckedValues(newCheckedValues);
      setItemQuestions(newItemQuestions);
      setFileList(newFileList);
    }
  };

  /**
   * Builds the answers array for a specific item by collecting all question answers
   * from form fields, internal state (checkedValues and selectedOptions), and subquestions
   * @param itemIndex - The index of the item in the itemsList array
   * @returns An array of answer objects with question IDs and answer values
   */
  const buildAnswersForItem = (itemIndex: number): Answer[] => {
    try {
      if (!itemsList[itemIndex] || !itemQuestions[itemIndex]) {
        return [];
      }

      const answers: Answer[] = [];
      const questions = itemQuestions[itemIndex];

      questions.forEach((question: Question) => {
        const fieldName = `item_${itemIndex}_question_${question.id}`;
        let answerValue: string | boolean | undefined;

        // Get answer based on field type
        switch (question.fieldType) {
          case 1: // Text input
            answerValue = form.getFieldValue(fieldName);
            break;
          case 2: // Checkbox
            answerValue = checkedValues[`${itemIndex}_${question.id}`] || "false";
            break;
          case 3: // Dropdown
            const selectedOptionId = selectedOptions[`${itemIndex}_${question.id}`];
            if (selectedOptionId) {
              const selectedOption = question.options?.find((opt: any) => opt.id === selectedOptionId);
              answerValue = selectedOption?.name || selectedOptionId;
            }
            break;
          default:
            answerValue = form.getFieldValue(fieldName);
        }

        // Add main question answer if it has a value
        if (answerValue !== undefined && answerValue !== null && answerValue !== '') {
          answers.push({
            question: question.id,
            answer: answerValue
          });
        }

        // Handle subquestions
        if (question.options && question.options.length > 0) {
          question.options.forEach((option: any) => {
            // For checkbox questions, only process subquestions if checkbox is checked and option name is "true"
            if (question.fieldType === 2) {
              if (checkedValues[`${itemIndex}_${question.id}`] === "true" && option.name === "true") {
                option.subQuestion?.forEach((subQ: any) => {
                  const subFieldName = `item_${itemIndex}_subQuestion_${subQ.id}`;
                  const subAnswerValue = form.getFieldValue(subFieldName);

                  if (subAnswerValue !== undefined && subAnswerValue !== null && subAnswerValue !== '') {
                    answers.push({
                      question: subQ.id,
                      answer: subAnswerValue
                    });
                  }
                });
              }
            }
            // For dropdown questions, only process subquestions if the option is selected
            else if (question.fieldType === 3) {
              const selectedOptionId = selectedOptions[`${itemIndex}_${question.id}`];
              if (selectedOptionId === option.id) {
                option.subQuestion?.forEach((subQ: any) => {
                  const subFieldName = `item_${itemIndex}_subQuestion_${subQ.id}`;
                  const subAnswerValue = form.getFieldValue(subFieldName);

                  if (subAnswerValue !== undefined && subAnswerValue !== null && subAnswerValue !== '') {
                    answers.push({
                      question: subQ.id,
                      answer: subAnswerValue
                    });
                  }
                });
              }
            }
          });
        }
      });

      return answers;
    } catch (error) {
      console.error('Error in buildAnswersForItem:', error);
      return [];
    }
  };

  /**
   * Updates all items with their latest answers before form submission
   * This ensures that the form data is properly synchronized with our internal state
   */
  const updateAllItemsAnswers = (): Item[] => {
    try {
      const updatedItems = itemsList.map((item: Item, index: number) => {
        // Build the latest answers for this item
        const answers = buildAnswersForItem(index);
        return {
          ...item,
          answers
        };
      });

      // Update the itemsList state
      setItemsList(updatedItems);

      // Update the form's items field
      form.setFieldsValue({
        items: updatedItems
      });

      return updatedItems;
    } catch (error) {
      console.error('Error in updateAllItemsAnswers:', error);
      return itemsList;
    }
  };

  return (
    <FormWrapper id="item-details-form" form={form} layout="vertical">
      <Form.Item name="deletedFiles" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="deletedItems" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="items" hidden>
        <Input />
      </Form.Item>

      <Collapse
        defaultActiveKey={itemsList.map((_, index) => index.toString())}
        key={`collapse-${itemsList.length}`}
      >
        {itemsList.map((item: any, itemIndex: number) => (
          <Collapse.Panel
            header={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>{`Item ${itemIndex + 1}`}</span>
                {itemsList.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(itemIndex);
                    }}
                    size="small"
                  />
                )}
              </div>
            }
            key={itemIndex}
          >
            <Row gutter={16} className="row-gap">
              <Col span={24} md={12}>
                <Dropdown
                  fontWeight="700"
                  label={"Category"}
                  name={`item_${itemIndex}_category`}
                  loading={categoryStatus === "loading"}
                  options={sortOptionsAlphabetically(categoryOptions, "label")}
                  rules={[
                    {
                      required: true,
                      message: "Please select a category!",
                    },
                  ]}
                  onSelect={(value) => onChangeCategory(itemIndex, value)}
                />
              </Col>
              <Col flex="auto" md={12}>
                <Dropdown
                  fontWeight="700"
                  label={"Sub Category"}
                  name={`item_${itemIndex}_subCategory`}
                  loading={subCategoryStatus === "loading"}
                  options={sortOptionsAlphabetically(getSubCategoryOptions(itemIndex), "label")}
                  rules={[
                    {
                      required: true,
                      message: "Please select a sub category!",
                    },
                  ]}
                  onSelect={(value) => onChangeSubCategory(itemIndex, value)}
                />
              </Col>
            </Row>

            <Row gutter={16} className="row-gap">
              <Col span={24} md={24}>
                {questionLoading ? (
                  <Spin />
                ) : (
                  itemQuestions[itemIndex] && (
                    <>
                      {itemQuestions[itemIndex].map((question: Question) => {
                        const fieldName = `item_${itemIndex}_question_${question.id}`;

                        switch (question.fieldType) {
                          case 1: // Text or Date
                            return (
                              <TextArea
                                key={question.id}
                                label={question.fieldName}
                                name={fieldName}
                                placeholder={question.placeholder || "Enter answer"}
                                rules={[{ required: question.isRequired, message: "Please enter answer" }]}
                                fontWeight={question.isRequired ? "700" : "400"}
                              />
                            );
                          case 2:
                            return (
                              <div key={question.id}>
                                <Form.Item
                                  name={fieldName}
                                  valuePropName="checked"
                                  rules={[{ required: question.isRequired, message: "Please enter answer" }]}
                                  initialValue={checkedValues[`${itemIndex}_${question.id}`] === "true"}
                                >
                                  <Checkbox
                                    checked={checkedValues[`${itemIndex}_${question.id}`] === "true"}
                                    style={{ fontWeight: question.isRequired ? "700" : "400" }}
                                    onChange={(e) => handleCheckboxChange(itemIndex, question.id, e)}
                                  >
                                    {question.fieldName}
                                  </Checkbox>
                                </Form.Item>
                                {question?.options && question?.options.length > 0 && (
                                  <>
                                    {question?.options?.map((option) => (
                                      <div style={{ width: "100%" }} key={option.id}>
                                        {checkedValues[`${itemIndex}_${question.id}`] === "true" && option.name === "true" && option?.subQuestion?.map((subQ: any) => (
                                          <TextArea
                                            key={subQ.id}
                                            label={subQ.fieldName}
                                            name={`item_${itemIndex}_subQuestion_${subQ.id}`}
                                            placeholder={subQ.placeholder || "Enter answer"}
                                            rules={[{ required: subQ.isRequired, message: `Please enter ${subQ.fieldName}` }]}
                                            fontWeight={subQ.isRequired ? "700" : "400"}
                                            onChange={(e: any) => {
                                              const value = e.target.value;
                                              form.setFieldsValue({ [`item_${itemIndex}_subQuestion_${subQ.id}`]: value });

                                              // Update the item's answers array for subquestions
                                              setItemsList(prev => prev.map((item, idx) => {
                                                if (idx === itemIndex) {
                                                  // Find if there's an existing answer for this subquestion
                                                  const existingAnswerIndex = item.answers?.findIndex((a: any) => a.question === subQ.id);
                                                  const newAnswers = [...(item.answers || [])];

                                                  if (existingAnswerIndex !== -1 && existingAnswerIndex !== undefined) {
                                                    // Update existing answer
                                                    newAnswers[existingAnswerIndex] = {
                                                      ...newAnswers[existingAnswerIndex],
                                                      answer: value
                                                    };
                                                  } else {
                                                    // Add new answer
                                                    newAnswers.push({
                                                      question: subQ.id,
                                                      answer: value
                                                    });
                                                  }

                                                  return { ...item, answers: newAnswers };
                                                }
                                                return item;
                                              }));
                                            }}
                                          />
                                        ))}
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            );
                          case 3:
                            const questionOptions = question?.options?.map((option) => ({
                              label: option.name,
                              value: option.id,
                              key: option.id,
                            })) || [];
                            const matchingSubQuestions = question?.options?.find((opt) => opt?.id === selectedOptions[`${itemIndex}_${question?.id}`])?.subQuestion || [];

                            return (
                              <div key={question.id}>
                                <Dropdown
                                  fontWeight={question.isRequired ? "700" : "400"}
                                  label={question.fieldName}
                                  name={fieldName}
                                  options={sortOptionsAlphabetically(questionOptions, "label") || []}
                                  rules={[{ required: question.isRequired, message: "Please enter answer" }]}
                                  onSelect={(value) => handleSelectChange(itemIndex, question.id, value)}
                                />

                                {selectedOptions[`${itemIndex}_${question?.id}`] && matchingSubQuestions?.length > 0 && matchingSubQuestions?.map((subQ: any) => (
                                  <TextArea
                                    key={subQ.id}
                                    label={subQ.fieldName}
                                    name={`item_${itemIndex}_subQuestion_${subQ.id}`}
                                    placeholder={subQ.placeholder || "Enter answer"}
                                    rules={[{ required: subQ.isRequired, message: `Please enter ${subQ.fieldName}` }]}
                                    fontWeight={subQ.isRequired ? "700" : "400"}
                                    onChange={(e: any) => {
                                      const value = e.target.value;
                                      form.setFieldsValue({ [`item_${itemIndex}_subQuestion_${subQ.id}`]: value });

                                      // Update the item's answers array for subquestions
                                      setItemsList(prev => prev.map((item, idx) => {
                                        if (idx === itemIndex) {
                                          // Find if there's an existing answer for this subquestion
                                          const existingAnswerIndex = item.answers?.findIndex((a: any) => a.question === subQ.id);
                                          const newAnswers = [...(item.answers || [])];

                                          if (existingAnswerIndex !== -1 && existingAnswerIndex !== undefined) {
                                            // Update existing answer
                                            newAnswers[existingAnswerIndex] = {
                                              ...newAnswers[existingAnswerIndex],
                                              answer: value
                                            };
                                          } else {
                                            // Add new answer
                                            newAnswers.push({
                                              question: subQ.id,
                                              answer: value
                                            });
                                          }

                                          return { ...item, answers: newAnswers };
                                        }
                                        return item;
                                      }));
                                    }}
                                  />
                                ))}
                              </div>
                            );
                          default:
                            return null;
                        }
                      })}
                    </>
                  )
                )}
              </Col>
            </Row>

            <TextArea
              label="Item Description"
              name={`item_${itemIndex}_itemDescription`}
              placeholder="Item Description"
              rules={[{ required: false, message: "Please enter Item Description!" }]}
              fontWeight="400"
              onChange={(e: any) => {
                const capitalizedValue = capitalizeWord(e.target.value);
                form.setFieldsValue({ [`item_${itemIndex}_itemDescription`]: capitalizedValue });
              }}
            />

            <Form.Item
              label={<Label fontWeight="400">Item images</Label>}
              name={`item_${itemIndex}_documents`}
              rules={[{ validator: validateUploadFiles(form) }]}
            >
              <Upload
                listType="picture-card"
                fileList={fileList[itemIndex] || []}
                onChange={(info) => handleFileChange(itemIndex, info)}
                beforeUpload={beforeUpload}
                maxCount={3}
                multiple={true}
                onPreview={handlePreview}
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                  showDownloadIcon: false,
                }}
                onRemove={(file) => handleRemove(itemIndex, file)}
                accept={imageUploadAccept}
              >
                {(fileList[itemIndex]?.length || 0) < 3 && "+ Upload"}
              </Upload>
            </Form.Item>

            {/* <Row gutter={16} className="row-gap">
              <Col span={24}>
                <TextArea
                  label="Document Description"
                  name={`item_${itemIndex}_documentDescription`}
                  placeholder="Enter a Document Description"
                  rules={[{ required: false, message: "Please enter Document Description!" }]}
                  fontWeight="400"
                  onChange={(e: any) => {
                    const capitalizedValue = capitalizeWord(e.target.value);
                    form.setFieldsValue({ [`item_${itemIndex}_documentDescription`]: capitalizedValue });
                  }}
                />
              </Col>
            </Row> */}
          </Collapse.Panel>
        ))}
      </Collapse>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Button
            type="dashed"
            onClick={onAddItem}
            icon={<PlusOutlined />}
            style={{ width: '100%' }}
          >
            Add New Item
          </Button>
        </Col>
      </Row>

    </FormWrapper>
  );
};

export default ItemDetailsForm;
